import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';
import 'firebase/compat/storage';
import FranchiseAPI from './franchiseAPI';
import { loginGreeting } from '../../lib/notifications/notificationsApi';
import { buildFullName, isNameDerivedFromEmail, resolveUserDisplayName } from '../../utils/userName';

const app = firebase;

// Use the Firebase Storage bucket (not the custom one)
const normalizedStorageBucket = 'douglas-924ae.firebasestorage.app';

const config = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: normalizedStorageBucket,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

class Firebase {
  constructor() {
    app.initializeApp(config);

    /* Helper */

    this.fieldValue = app.firestore.FieldValue;
    this.emailAuthProvider = app.auth.EmailAuthProvider;

    /* Firebase APIs */

    this.auth = app.auth();
    this.db = app.firestore();
    this.functions = app.functions();
    this.storage = app.storage();

    /* Social Sign In Method Provider */

    this.googleProvider = new app.auth.GoogleAuthProvider();
    this.facebookProvider = new app.auth.FacebookAuthProvider();
    this.twitterProvider = new app.auth.TwitterAuthProvider();

    /* Franchise API */
    this.franchiseAPI = new FranchiseAPI(this.db, this.fieldValue);

    // Attach pending DISC assessments after login
    this.attachPendingDiscAssessments = () =>
      this.functions.httpsCallable('attachPendingDiscAssessments')().catch(() => null);

    // Register login greetings + notification profile
    this.triggerLoginGreeting = () => {
      if (typeof window === 'undefined') return;
      const timezone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || null;
      const tzOffsetMinutes = -new Date().getTimezoneOffset();
      return loginGreeting(this, {
        timezone,
        tzOffsetMinutes,
        productName: 'Douglas & McCartney CRM',
      }).catch(() => null);
    };
  }

  // *** Auth API ***

  doCreateUserWithEmailAndPassword = (email, password) =>
    this.auth.createUserWithEmailAndPassword(email, password);

  doSignInWithEmailAndPassword = (email, password) =>
    this.auth.signInWithEmailAndPassword(email, password);

  doSignInWithGoogle = () =>
    this.auth.signInWithPopup(this.googleProvider);

  doSignInWithFacebook = () =>
    this.auth.signInWithPopup(this.facebookProvider);

  doSignInWithTwitter = () =>
    this.auth.signInWithPopup(this.twitterProvider);

  doSignOut = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('authUser');
      window.localStorage.removeItem('lastLoginAt');
    }
    return this.auth.signOut();
  };

  doPasswordReset = email => this.auth.sendPasswordResetEmail(email);

  doSendEmailVerification = async () => {
    const isLocalHost =
      typeof window !== 'undefined' &&
      ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

    if (isLocalHost) {
      if (!this.auth.currentUser) {
        throw new Error('No authenticated user available for email verification');
      }
      return this.auth.currentUser.sendEmailVerification({
        url: process.env.REACT_APP_CONFIRMATION_EMAIL_REDIRECT,
      });
    }

    const sendBrandedVerificationEmail = this.functions.httpsCallable('sendBrandedVerificationEmail');
    try {
      return await sendBrandedVerificationEmail();
    } catch (error) {
      // Never block signup on Cloud Function/CORS/network issues.
      // Fall back to Firebase's native verification email as a safety path.
      if (typeof console !== 'undefined') {
        console.warn('Branded verification email failed, using Firebase fallback.', {
          code: error?.code || null,
          message: error?.message || null,
        });
      }

      if (!this.auth.currentUser) {
        throw error;
      }

      return this.auth.currentUser.sendEmailVerification({
        url: process.env.REACT_APP_CONFIRMATION_EMAIL_REDIRECT,
      });
    }
  };

  doPasswordUpdate = password =>
    this.auth.currentUser.updatePassword(password);

  // *** Merge Auth and DB User API *** //

  onAuthUserListener = (next, fallback) =>
    this.auth.onAuthStateChanged(firebaseAuthUser => {
      if (firebaseAuthUser) {
        this.user(firebaseAuthUser.uid)
          .get()
          .then(snapshot => {
            let dbUser = snapshot.data();

            // If user doesn't exist in Firestore yet, create empty object
            if (!dbUser) {
              dbUser = {};
            }

            // default empty roles
            if (!dbUser.roles) {
              dbUser.roles = {};
            }

            const providerDisplayName = (firebaseAuthUser.providerData || [])
              .map((provider) => (typeof provider?.displayName === 'string' ? provider.displayName.trim() : ''))
              .find(Boolean);
            const authDisplayName = providerDisplayName || firebaseAuthUser.displayName || '';
            const isGoogleUser = (firebaseAuthUser.providerData || []).some(
              (provider) => provider?.providerId === 'google.com',
            );

            const googleProfileUpdates = {};
            if (isGoogleUser && authDisplayName) {
              if (!dbUser.signupMethod) {
                googleProfileUpdates.signupMethod = 'google';
              }
              if (!dbUser.fullName || isNameDerivedFromEmail(dbUser.fullName, firebaseAuthUser.email)) {
                googleProfileUpdates.fullName = authDisplayName;
              }
              if (!dbUser.username || isNameDerivedFromEmail(dbUser.username, firebaseAuthUser.email)) {
                googleProfileUpdates.username = authDisplayName;
              }
            }

            if (Object.keys(googleProfileUpdates).length > 0) {
              this.user(firebaseAuthUser.uid).set(googleProfileUpdates, { merge: true }).catch(() => null);
              dbUser = {
                ...dbUser,
                ...googleProfileUpdates,
              };
            }

            const mergedUser = {
              uid: firebaseAuthUser.uid,
              email: firebaseAuthUser.email,
              emailVerified: firebaseAuthUser.emailVerified,
              providerData: firebaseAuthUser.providerData || [],
              authDisplayName,
              ...dbUser,
              providerData: firebaseAuthUser.providerData || [],
              authDisplayName,
            };

            const inferredFullName =
              mergedUser.fullName || buildFullName(mergedUser.firstName, mergedUser.lastName);
            const normalizedAuthUser = {
              ...mergedUser,
              fullName: inferredFullName,
              displayName: resolveUserDisplayName({
                ...mergedUser,
                fullName: inferredFullName,
              }),
            };

            next(normalizedAuthUser);

            // Background sync of DISC assessments for this user
            this.attachPendingDiscAssessments();

            // Ensure notifications profile + greetings on real login
            this.triggerLoginGreeting();
          });
      } else {
        fallback();
      }
    });

  // *** User API ***

  user = uid => this.db.doc(`users/${uid}`);

  users = () => this.db.collection('users');

  // *** Message API ***

  message = uid => this.db.doc(`messages/${uid}`);

  messages = () => this.db.collection('messages');
}

export default Firebase;
